import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Trophy, Medal, Calendar, Gamepad2, User, Users, Crown, Coins, X, Pencil, Trash2, SlidersHorizontal, ChevronDown } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import EditTournamentModal from "../components/EditTournamentModal";
import { api, resolveAsset, formatError } from "../lib/api";
import { useAuth } from "../lib/auth";

const DISCORD_URL = "https://discord.gg/nEvwBUtqAV";

function initials(n) {
  if (!n) return "?";
  return n.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch (_) { return "—"; }
}

export default function HallOfFamePage() {
  const { user } = useAuth();
  const [data, setData] = useState([]); // [{tournament, teams, winner, runnerUp}]
  const [game, setGame] = useState("all");
  const [host, setHost] = useState("all");
  const [winner, setWinner] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const activeFiltersCount =
    (game !== "all") + (host !== "all") + (winner !== "all") + (yearFilter !== "all");

  const clearFilters = () => {
    setGame("all");
    setHost("all");
    setWinner("all");
    setYearFilter("all");
  };

  const load = async () => {
    const { data: ts } = await api.get("/tournaments");
    const rows = [];
    await Promise.all(
      ts.map(async (t) => {
        const { data: detail } = await api.get(`/tournaments/${t.id}`);
        const winnerTeam = detail.teams.find((tm) => tm.id === t.winner_team_id) || null;
        const runnerTeam = detail.teams.find((tm) => tm.id === t.runner_up_team_id) || null;
        const finalMatch = detail.matches
          .filter((m) => !m.next_match_id || m.bracket === "grand")
          .sort((a, b) => (b.round || 0) - (a.round || 0))[0];
        rows.push({
          tournament: t,
          teams: detail.teams,
          matches: detail.matches,
          winnerTeam,
          runnerTeam,
          finalMatch,
        });
      })
    );
    rows.sort((a, b) => {
      const ad = new Date(a.tournament.ended_at || a.tournament.starts_at || a.tournament.created_at).getTime();
      const bd = new Date(b.tournament.ended_at || b.tournament.starts_at || b.tournament.created_at).getTime();
      return bd - ad;
    });
    setData(rows);
  };

  useEffect(() => { load(); }, []);

  const canManage = (t) => {
    if (!user || !user.id) return false;
    if (user.role === "admin") return true;
    return Boolean(t.owner_id) && t.owner_id === user.id;
  };

  // Build filter options
  const gameOpts = useMemo(() => {
    const set = new Set();
    data.forEach((r) => r.tournament.game && set.add(r.tournament.game));
    return Array.from(set);
  }, [data]);
  const hostOpts = useMemo(() => {
    const set = new Set();
    data.forEach((r) => r.tournament.host && set.add(r.tournament.host));
    return Array.from(set);
  }, [data]);
  const winnerOpts = useMemo(() => {
    const set = new Set();
    data.forEach((r) => r.winnerTeam && set.add(r.winnerTeam.name));
    return Array.from(set);
  }, [data]);
  const yearOpts = useMemo(() => {
    const set = new Set();
    data.forEach((r) => {
      const d = r.tournament.ended_at || r.tournament.starts_at || r.tournament.created_at;
      if (d) set.add(new Date(d).getFullYear());
    });
    return Array.from(set).sort((a, b) => b - a);
  }, [data]);

  const filtered = data.filter((r) => {
    if (game !== "all" && r.tournament.game !== game) return false;
    if (host !== "all" && r.tournament.host !== host) return false;
    if (winner !== "all" && (r.winnerTeam?.name || "") !== winner) return false;
    if (yearFilter !== "all") {
      const d = r.tournament.ended_at || r.tournament.starts_at || r.tournament.created_at;
      if (!d || String(new Date(d).getFullYear()) !== String(yearFilter)) return false;
    }
    return true;
  });

  const stats = {
    total: data.length,
    champions: new Set(data.filter((r) => r.winnerTeam).map((r) => r.winnerTeam.name)).size,
    prize: data.reduce((a, r) => a + (r.tournament.prize_pool || 0), 0),
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-red-400 font-mono">Legacy archive</div>
          <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mt-2">Hall of Fame</h1>
          <p className="text-zinc-400 mt-3 max-w-2xl">
            Every tournament ever hosted on Spicy Community. Champions, runners-up, MVPs and bracket history.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          <StatTile label="Tournaments" value={stats.total} />
          <StatTile label="Champions" value={stats.champions} />
          <StatTile label="Total Prize" value={`$${stats.prize.toLocaleString()}`} highlight />
        </div>

        {/* Filters — compact toggle bar; expands on tap */}
        <div className="mb-6">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            data-testid="hof-filters-toggle"
            className="w-full glass-dark rounded-xl px-4 py-2.5 flex items-center justify-between gap-3 text-sm hover:border-red-500/30 transition-colors"
          >
            <span className="flex items-center gap-2 text-zinc-300">
              <SlidersHorizontal size={14} className="text-red-400" />
              <span className="font-semibold">Filters</span>
              {activeFiltersCount > 0 && (
                <span className="inline-flex items-center justify-center text-[10px] font-mono font-bold rounded-full bg-red-500/20 border border-red-500/40 text-red-200 px-1.5 min-w-[20px] h-[18px]">
                  {activeFiltersCount}
                </span>
              )}
            </span>
            <span className="flex items-center gap-2">
              {activeFiltersCount > 0 && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); clearFilters(); }}
                  data-testid="hof-filters-clear"
                  className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-red-300 font-mono"
                >
                  Clear
                </span>
              )}
              <ChevronDown
                size={14}
                className={`text-zinc-500 transition-transform ${filtersOpen ? "rotate-180" : ""}`}
              />
            </span>
          </button>
          {filtersOpen && (
            <div className="glass-dark rounded-xl p-3 mt-2 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <FilterSelect label="Game" value={game} onChange={setGame} options={gameOpts} tid="hof-game" />
              <FilterSelect label="Year" value={yearFilter} onChange={setYearFilter} options={yearOpts} tid="hof-year" />
              <FilterSelect label="Host" value={host} onChange={setHost} options={hostOpts} tid="hof-host" />
              <FilterSelect label="Winner" value={winner} onChange={setWinner} options={winnerOpts} tid="hof-winner" />
            </div>
          )}
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map((r) => (
            <div
              key={r.tournament.id}
              data-testid={`hof-card-${r.tournament.id}`}
              className="group relative cursor-pointer glass-dark rounded-2xl overflow-hidden hover:border-red-500/40 hover:shadow-[0_0_28px_rgba(230,0,0,0.25)] transition-all"
            >
              {canManage(r.tournament) && (
                <div className="absolute top-3 right-3 z-10 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditing(r); }}
                    data-testid={`hof-edit-${r.tournament.id}`}
                    className="h-7 w-7 rounded-full bg-black/70 backdrop-blur flex items-center justify-center text-white hover:text-red-300"
                    title="Edit"
                  >
                    <Pencil size={11} />
                  </button>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!window.confirm(`Delete "${r.tournament.name}"?`)) return;
                      try {
                        await api.delete(`/tournaments/${r.tournament.id}`);
                        toast.success("Deleted");
                        await load();
                      } catch (err) { toast.error(formatError(err.response?.data?.detail) || err.message); }
                    }}
                    data-testid={`hof-delete-${r.tournament.id}`}
                    className="h-7 w-7 rounded-full bg-black/70 backdrop-blur flex items-center justify-center text-white hover:text-red-400"
                    title="Delete"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              )}
              <div onClick={() => setSelected(r)} className="cursor-pointer">
              <div
                className="h-40 relative bg-cover bg-center"
                style={{
                  backgroundImage: r.tournament.banner_url
                    ? `url(${resolveAsset(r.tournament.banner_url)})`
                    : "linear-gradient(135deg, rgba(230,0,0,0.35), rgba(20,0,0,0.8))",
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-[10px] uppercase tracking-widest font-mono ${
                    r.tournament.status === "finished" ? "bg-emerald-900/50 text-emerald-300 border border-emerald-500/30" :
                    r.tournament.status === "live" ? "bg-red-900/50 text-red-300 border border-red-500/30" :
                    "bg-zinc-800/60 text-zinc-300 border border-white/10"
                  }`}>
                    {r.tournament.status}
                  </span>
                  {r.tournament.game && (
                    <span className="px-2 py-1 rounded-full text-[10px] font-mono text-white bg-black/50 border border-white/10 flex items-center gap-1">
                      <Gamepad2 size={10} /> {r.tournament.game}
                    </span>
                  )}
                </div>
                {r.winnerTeam && (
                  <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2">
                    <Crown size={16} className="text-yellow-400" />
                    <span className="font-display font-bold text-white text-sm drop-shadow-lg">
                      {r.winnerTeam.name}
                    </span>
                    <span className="text-yellow-400 text-xs font-mono uppercase tracking-widest ml-auto drop-shadow-lg">CHAMPION</span>
                  </div>
                )}
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-display font-bold text-xl leading-tight">{r.tournament.name}</h3>
                </div>
                <div className="flex items-center gap-3 text-xs text-zinc-400 mb-4">
                  <span className="flex items-center gap-1"><Calendar size={12} /> {formatDate(r.tournament.ended_at || r.tournament.starts_at)}</span>
                  <span className="flex items-center gap-1"><Coins size={12} /> ${(r.tournament.prize_pool || 0).toLocaleString()}</span>
                  {r.tournament.host && (
                    <span className="flex items-center gap-1"><User size={12} /> {r.tournament.host}</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <TeamSlot label="Champion" team={r.winnerTeam} medal="gold" />
                  <TeamSlot label="Runner-up" team={r.runnerTeam} medal="silver" />
                </div>
                {r.tournament.mvp && (
                  <div className="mt-3 pt-3 border-t border-white/5 text-xs flex items-center gap-2 text-zinc-400">
                    <Medal size={12} className="text-red-400" /> MVP: <span className="text-white font-semibold">{r.tournament.mvp}</span>
                  </div>
                )}
              </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="md:col-span-2 glass-dark rounded-2xl p-12 text-center text-zinc-500">
              No tournaments match your filters.
            </div>
          )}
        </div>
      </div>

      {selected && <HofDetailModal row={selected} onClose={() => setSelected(null)} />}
      {editing && (
        <EditTournamentModal
          tournament={editing.tournament}
          teams={editing.teams}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
          onDeleted={async () => { setEditing(null); await load(); }}
        />
      )}
      <Toaster position="top-right" theme="dark" toastOptions={{ className: "glass-dark !border-red-500/30" }} />
      <Footer discordUrl={DISCORD_URL} />
    </div>
  );
}

function StatTile({ label, value, highlight }) {
  return (
    <div className={`rounded-2xl p-5 ${highlight ? "glass-red-glow" : "glass-dark"}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
      <div className="font-display font-black text-2xl md:text-3xl mt-1">{value}</div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, tid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</span>
      <select
        data-testid={tid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2 focus:outline-none focus:border-red-500 text-sm"
      >
        <option value="all">{`All ${label.toLowerCase()}s`}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function TeamSlot({ label, team, medal }) {
  const color = medal === "gold" ? "#FFD700" : "#C0C0C0";
  return (
    <div className="glass-red rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-widest font-mono mb-2" style={{ color }}>{label}</div>
      {team ? (
        <div className="flex items-center gap-2">
          {team.logo_url ? (
            <img src={resolveAsset(team.logo_url)} alt="" className="h-7 w-7 rounded-full object-cover border border-white/10" />
          ) : (
            <div className="h-7 w-7 rounded-full flex items-center justify-center font-display font-bold text-xs" style={{ background: team.color || "#E60000" }}>
              {initials(team.name)}
            </div>
          )}
          <span className="font-medium truncate text-sm">{team.name}</span>
        </div>
      ) : (
        <div className="text-zinc-600 text-sm">TBD</div>
      )}
    </div>
  );
}

function HofDetailModal({ row, onClose }) {
  const { tournament: t, teams, matches, winnerTeam, runnerTeam } = row;
  const byId = {};
  teams.forEach((te) => (byId[te.id] = te));
  const finalMatch = matches
    .filter((m) => !m.next_match_id || m.bracket === "grand")
    .sort((a, b) => (b.round || 0) - (a.round || 0))[0];

  return (
    <div onClick={onClose} className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-auto">
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="hof-detail-modal"
        className="glass-red-glow rounded-2xl max-w-2xl w-full relative"
      >
        <button
          onClick={onClose}
          data-testid="close-hof-modal"
          className="absolute top-4 right-4 h-8 w-8 rounded-full glass-dark flex items-center justify-center hover:text-red-400 z-10"
        >
          <X size={14} />
        </button>
        <div
          className="h-52 rounded-t-2xl bg-cover bg-center relative"
          style={{
            backgroundImage: t.banner_url
              ? `url(${resolveAsset(t.banner_url)})`
              : "linear-gradient(135deg, rgba(230,0,0,0.35), rgba(20,0,0,0.8))",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent rounded-t-2xl" />
          <div className="absolute bottom-4 left-6 right-6">
            {t.game && (
              <div className="text-xs uppercase tracking-[0.3em] text-red-300 font-mono mb-1">{t.game}</div>
            )}
            <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight">{t.name}</h2>
          </div>
        </div>
        <div className="p-6 md:p-8 space-y-5">
          <div className="flex flex-wrap gap-4 text-xs text-zinc-400">
            <span className="flex items-center gap-1"><Calendar size={12} /> {formatDate(t.ended_at || t.starts_at)}</span>
            <span className="flex items-center gap-1"><Coins size={12} className="text-red-400" /> <span className="text-red-300 font-mono font-bold">${(t.prize_pool || 0).toLocaleString()}</span></span>
            {t.host && <span className="flex items-center gap-1"><User size={12} /> Host: {t.host}</span>}
            <span className="flex items-center gap-1"><Users size={12} /> {teams.length} teams</span>
          </div>
          {t.description && <p className="text-zinc-300 text-sm">{t.description}</p>}
          <div className="grid grid-cols-2 gap-3">
            <TeamSlot label="Champion" team={winnerTeam} medal="gold" />
            <TeamSlot label="Runner-up" team={runnerTeam} medal="silver" />
          </div>
          {t.mvp && (
            <div className="glass-dark rounded-xl p-4 flex items-center gap-3">
              <Medal size={18} className="text-red-400" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Tournament MVP</div>
                <div className="font-display font-bold text-lg">{t.mvp}</div>
              </div>
            </div>
          )}
          {/* Final Bracket Result block removed by request — group_advancement
              and other formats don't surface the expected finalMatch fields,
              so the section mostly showed "TBD — 0" which was useless. */}
          <Link
            to={`/tournaments/${t.id}`}
            onClick={onClose}
            className="btn-liquid inline-flex w-full justify-center"
          >
            <Trophy size={16} /> View full bracket
          </Link>
        </div>
      </div>
    </div>
  );
}
